"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useLanguage } from '@/context/LanguageContext';
import { Lock, Zap, ShieldCheck, Cpu } from 'lucide-react';
import { FaXTwitter as Twitter, FaLinkedin as Linkedin, FaFacebook as Facebook, FaYoutube as Youtube } from 'react-icons/fa6';

export default function Footer() {
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState({
        loading: false,
        message: '',
        isError: false
    });

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setStatus({ loading: false, message: 'Please enter a valid email.', isError: true });
            return;
        }

        setStatus({ loading: true, message: '', isError: false });

        try {
            const res = await fetch('/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await res.json();

            if (res.ok) {
                setStatus({ loading: false, message: data.message || 'Subscribed successfully!', isError: false });
                setEmail('');
            } else {
                setStatus({ loading: false, message: data.error || 'Failed to subscribe.', isError: true });
            }
        } catch (err) {
            setStatus({ loading: false, message: 'Connection failed.', isError: true });
        }
    };

    return (
        <footer style={{
            background: '#fff',
            borderTop: '1px solid var(--border)',
            padding: '2.5rem 0 2rem'
        }}>
            <div className="container">
                {/* Upper Footer: Branding & Newsletter */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: 'clamp(2rem, 5vw, 4rem)',
                    marginBottom: 'clamp(2rem, 5vw, 4rem)',
                    paddingBottom: 'clamp(2rem, 5vw, 4rem)',
                    borderBottom: '1px solid #f1f5f9'
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1.5rem' }}>
                            <Image
                                src="/logo.svg"
                                alt="CorpToolset Logo"
                                width={36}
                                height={36}
                                style={{
                                    objectFit: 'contain',
                                    display: 'block'
                                }}
                            />
                            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--primary)', margin: 0, letterSpacing: '-0.03em' }}>CorpToolset</div>
                        </div>
                        <p style={{ fontSize: '1rem', color: 'var(--secondary)', lineHeight: 1.6, marginBottom: '2rem' }}>
                            {t('footer_tagline')}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#334155', fontSize: '0.95rem' }}>
                                <span>📧</span> <strong>contact@corptoolset.com</strong>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#334155', fontSize: '0.95rem' }}>
                                <span>📍</span> <span>International Remote Operations Network</span>
                            </div>
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                                <a href="https://linkedin.com/company/corptoolset" target="_blank" rel="noopener noreferrer" className="social-link hover-scale" style={{ color: '#0077b5' }} aria-label="LinkedIn">
                                    <Linkedin size={28} />
                                </a>
                                <a href="https://facebook.com/corptoolset" target="_blank" rel="noopener noreferrer" className="social-link hover-scale" style={{ color: '#1877f2' }} aria-label="Facebook">
                                    <Facebook size={28} />
                                </a>
                                <a href="https://x.com/corptoolset" target="_blank" rel="noopener noreferrer" className="social-link hover-scale" style={{ color: '#000000' }} aria-label="X (Twitter)">
                                    <Twitter size={28} />
                                </a>
                                <a href="https://youtube.com/@corptoolset" target="_blank" rel="noopener noreferrer" className="social-link hover-scale" style={{ color: '#ff0000' }} aria-label="YouTube">
                                    <Youtube size={28} />
                                </a>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        background: '#f8fafc',
                        padding: 'clamp(1.25rem, 5vw, 2.5rem)',
                        borderRadius: '24px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem' }}>{t('stay_updated')}</div>
                        <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1.5rem' }}>{t('subscribe_desc')}</p>
                        <form style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column', width: '100%' }} onSubmit={handleSubscribe}>
                            <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t('enter_email')}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1rem',
                                        borderRadius: '10px',
                                        border: '1px solid rgb(203, 213, 225)',
                                        outline: 'none',
                                        fontSize: '0.95rem'
                                    }}
                                />
                                <button
                                    className="btn-primary"
                                    disabled={status.loading}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1.5rem',
                                        whiteSpace: 'nowrap',
                                        opacity: status.loading ? 0.7 : 1
                                    }}
                                >
                                    {status.loading ? '...' : t('subscribe_btn')}
                                </button>
                            </div>
                            {status.message && (
                                <p style={{
                                    fontSize: '0.85rem',
                                    marginTop: '0.5rem',
                                    color: status.isError ? '#ef4444' : '#10b981',
                                    fontWeight: 600
                                }}>
                                    {status.message}
                                </p>
                            )}
                        </form>
                    </div>
                </div>

                {/* Middle Footer: Links */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '2.5rem',
                    marginBottom: '4rem'
                }}>
                    <div>
                        <div style={{ marginBottom: '1.5rem', fontSize: '0.95rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0f172a' }}>{t('text_utilities')}</div>
                        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.9rem', padding: 0 }}>
                            <li><Link prefetch={false} href="/tools/sort-text-online" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('sort_text_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/find-and-replace-text" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('find_replace_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/online-word-counter" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('word_counter_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/text-difference-checker" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('diff_checker_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/remove-duplicate-text-lines" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('duplicate_remover_title')}</Link></li>
                        </ul>
                    </div>

                    <div>
                        <div style={{ marginBottom: '1.5rem', fontSize: '0.95rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0f172a' }}>{t('privacy_pdf')}</div>
                        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.9rem', padding: 0 }}>
                            <li><Link prefetch={false} href="/tools/merge-pdf" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('pdf_merge_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/split-pdf" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('pdf_split_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/json-formatter" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('json_formatter_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/password-generator" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('password_gen_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/username-generator" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('username_gen_title')}</Link></li>
                        </ul>
                    </div>

                    <div>
                        <div style={{ marginBottom: '1.5rem', fontSize: '0.95rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0f172a' }}>{t('cleaning_tools')}</div>
                        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.9rem', padding: 0 }}>
                            <li><Link prefetch={false} href="/tools/email-extractor-tool" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('email_extractor_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/url-extractor-tool" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('url_extractor_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/strip-html-tags-online" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('html_stripper_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/remove-emojis-from-text" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('emoji_remover_title')}</Link></li>
                            <li><Link prefetch={false} href="/tools/grammar-checker" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('grammar_checker_title')}</Link></li>
                        </ul>
                    </div>

                    <div>
                        <div style={{ marginBottom: '1.5rem', fontSize: '0.95rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0f172a' }}>{t('platform')}</div>
                        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.9rem', padding: 0 }}>
                            <li><Link prefetch={false} href="/tools" className="hover-primary" style={{ color: 'var(--primary)', fontWeight: 700 }}>{t('all_tools') || 'Tools Discovery Hub'}</Link></li>
                            <li><Link prefetch={false} href="/about" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('nav_about')}</Link></li>
                            <li><Link prefetch={false} href="/contact" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('nav_contact')}</Link></li>
                            <li><Link prefetch={false} href="/resources" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('nav_resources') || 'Resources & Standards'}</Link></li>
                            <li><Link prefetch={false} href="/privacy-architecture" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>Privacy Architecture</Link></li>
                            <li><Link prefetch={false} href="/privacy" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('privacy_policy')}</Link></li>
                            <li><Link prefetch={false} href="/cookies" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('nav_cookies') || 'Cookie Policy'}</Link></li>
                            <li><Link prefetch={false} href="/terms" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('terms_service')}</Link></li>
                            <li><Link prefetch={false} href="/disclaimer" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('disclaimer')}</Link></li>
                            <li><Link prefetch={false} href="/faq" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('nav_faq') || 'FAQ'}</Link></li>
                            <li><Link prefetch={false} href="/sitemap-html" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>{t('sitemap')}</Link></li>
                        </ul>
                    </div>

                    <div>
                        <div style={{ marginBottom: '1.5rem', fontSize: '0.95rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0f172a' }}>{t('popular_solutions') || 'Popular Solutions'}</div>
                        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.9rem', padding: 0 }}>
                            <li><Link prefetch={false} href="/tools/json-beautifier-online" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>JSON Beautifier</Link></li>
                            <li><Link prefetch={false} href="/tools/sort-text-online" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>A-Z List Sorter</Link></li>
                            <li><Link prefetch={false} href="/tools/secure-password-vault-gen" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>Password Vault Gen</Link></li>
                            <li><Link prefetch={false} href="/tools/html-to-text-converter" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>HTML to Text</Link></li>
                            <li><Link prefetch={false} href="/tools/json-formatter" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>CSV to JSON Pro</Link></li>
                        </ul>
                    </div>

                    <div>
                        <div style={{ marginBottom: '1.5rem', fontSize: '0.95rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0f172a' }}>{t('latest_blog')}</div>
                        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.85rem', fontSize: '0.9rem', padding: 0 }}>
                            <li><Link prefetch={false} href="/blog/password-best-practices" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>Password Security</Link></li>
                            <li><Link prefetch={false} href="/blog/boost-productivity-with-free-tools" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>Productivity Hacks</Link></li>
                            <li><Link prefetch={false} href="/blog/why-corptoolset" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>Why CorpToolset?</Link></li>
                            <li><Link prefetch={false} href="/blog/common-password-mistakes" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>Password Mistakes</Link></li>
                            <li><Link prefetch={false} href="/blog/save-time-and-money-online-tools" className="hover-primary" style={{ color: 'var(--secondary)', transition: 'color 0.2s' }}>Save Time & Money</Link></li>
                            <li><Link prefetch={false} href="/blog" style={{ color: 'var(--primary)', fontWeight: 700 }}>{t('browse_all')}</Link></li>
                        </ul>
                    </div>
                </div>

                {/* Lower Footer: Content & Copyright */}
                <div style={{ marginTop: '3.5rem', borderTop: '1px solid var(--border)', paddingTop: '3rem', color: 'var(--secondary)', lineHeight: '1.8' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem' }}>
                        <div>
                            <div style={{ color: '#1e293b', marginBottom: '1rem', fontWeight: 700 }}>{t('footer_why_title')}</div>
                            <p style={{ fontSize: '0.95rem' }}>{t('footer_why_desc')}</p>
                        </div>
                        <div>
                            <div style={{ color: '#1e293b', marginBottom: '1rem', fontWeight: 700 }}>{t('footer_features_title')}</div>
                            <ul style={{ fontSize: '0.95rem', paddingLeft: '1.25rem' }}>
                                <li>{t('footer_feature_1')}</li>
                                <li>{t('footer_feature_2')}</li>
                                <li>{t('footer_feature_3')}</li>
                                <li>{t('footer_feature_4')}</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div style={{
                    marginTop: '2.5rem',
                    background: '#0f172a',
                    borderRadius: '32px',
                    padding: 'clamp(2rem, 8vw, 4rem) 1.5rem',
                    color: 'white',
                    textAlign: 'center',
                    boxShadow: '0 20px 40px -10px rgba(15, 23, 42, 0.3)'
                }}>
                    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                        <p style={{ fontSize: 'var(--fs-lg)', fontWeight: 500, lineHeight: 1.6, color: '#f8fafc', marginBottom: '2.5rem' }}>
                            We built CorpToolset because we were sick of the ad-riddled fossils that currently pass for "free tools." If you want to dive into the technicals of our local-only runtime or just have a thought on how to make a tool better, we&apos;re all ears:
                        </p>
                        <a
                            href="/contact"
                            style={{
                                fontSize: 'clamp(1.25rem, 5vw, 1.75rem)',
                                fontWeight: 800,
                                color: '#ffffff',
                                textDecoration: 'none',
                                borderBottom: '3px solid var(--primary)',
                                paddingBottom: '4px',
                                transition: 'all 0.3s',
                                wordBreak: 'break-all'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary)'}
                            onMouseOut={(e) => e.currentTarget.style.color = '#ffffff'}
                        >
                            contact@corptoolset.com
                        </a>
                        <p style={{ marginTop: '2.5rem', fontSize: 'var(--fs-sm)', color: '#94a3b8', fontWeight: 500 }}>
                            Legal docs and privacy architecture can be a massive headache. If you&apos;ve got questions about ours, just reach out. We actually read these emails and usually get back to you within a day or so.
                        </p>
                    </div>
                </div>

                <div style={{ marginTop: '3rem', borderTop: '1px solid var(--border)', paddingTop: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
                    <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: 700, color: '#64748b' }}>
                            <Lock size={16} color="var(--primary)" />
                            RUNS IN YOUR BROWSER
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: 700, color: '#64748b' }}>
                            <Zap size={16} color="var(--primary)" />
                            POWERED BY WASM
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: 700, color: '#64748b' }}>
                            <ShieldCheck size={16} color="var(--primary)" />
                            NO TRACKING SCRIPTS
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: 700, color: '#64748b' }}>
                            <Cpu size={16} color="var(--primary)" />
                            ZERO SERVER LOGS
                        </div>
                    </div>
                    <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>
                        © <span suppressHydrationWarning>{new Date().getFullYear()}</span> {t('copyright')}
                    </div>
                </div>
            </div>
        </footer>
    );
}
